import { Injector } from "@angular/core";
import { HttpClient } from "@angular/common/http";

import {
  BrowserNotificationStrategy,
  NoAnalyticsStrategy,
  NoQueueStrategy,
  StandaloneInvitationStrategy,
  TalkdeskInteractionStrategy,
} from "../strategies";

import {
  OAuth2CodeGrantAuthenticationStrategy,
  IInteractionStrategy,
  IAnalyticsStrategy,
  IAuthenticationStrategy,
  IIntegrationStrategy,
  IQueueStrategy,
  IInvitationStrategy,
  INotificationStrategy,
  IConversationStrategy,
} from "../../../core-ui/models/strategies";
import {
  AuviousRtcService,
  AppConfigService,
  UserService,
  ProtectedTicketService,
  NotificationService,
  ActivityIndicatorService,
} from "../../../core-ui/services";
import {
  IApplication,
  IApplicationDTO,
  IOpenIDConnectApplicationConfig,
  ITalkdeskApplicationConfig,
} from "../../../core-ui/models/IApplication";
import {
  ApplicationTypeEnum,
} from "../../../core-ui/core-ui.enums";
import { loadScript } from "../../app.utils";
import {
  TalkdeskIntegrationStrategy,
  TalkdeskConversationStrategy,
} from "../strategies/talkdesk";
import { OAUTH2_CODE_GRAND_REDIRECT_URI } from "../../../core-ui/core-ui.enums";
import { DigitalConnectService } from "../../services";
import { AgentParam } from "../../../core-ui";
import { localStore, sessionStore } from "@auvious/utils";

// Define the constant for the CSRF token header
export const TALKDESK_CSRF_TOKEN_HEADER = "X-Talkdesk-Refresh-Csrf-Token";
export const TALKDESK_CSRF_TOKEN_KEY = "talkdesk-csrf-token";

export class TalkdeskApplication implements IApplication {
  private mAuthenticationStrategy: IAuthenticationStrategy;
  private mInteractionStrategy: IInteractionStrategy;
  private mAnalyticsStrategy: IAnalyticsStrategy;
  private mQueueStrategy: IQueueStrategy;
  private mTicketStrategy: IInvitationStrategy;
  private mNotificationStrategy: INotificationStrategy;
  private mIntegrationStrategy: IIntegrationStrategy;
  private mConversationStrategy: IConversationStrategy;

  private appConfigService: AppConfigService;
  private userService: UserService;

  constructor(private dto: IApplicationDTO, private injector: Injector) {
    this.appConfigService = injector.get(AppConfigService);
    this.appConfigService.setApplicationConfig(dto.config);
    this.userService = injector.get(UserService);

    this.mAuthenticationStrategy = new OAuth2CodeGrantAuthenticationStrategy(
      this.injector.get(AuviousRtcService),
      this.injector.get(HttpClient),
      this.injector.get(NotificationService),
      this.injector.get(ActivityIndicatorService),
      {
        clientId: "talkdesk",
        redirectUri: window.location.origin + OAUTH2_CODE_GRAND_REDIRECT_URI,
        authProvider: async () => {
          const config = this.dto.config as ITalkdeskApplicationConfig;
          return {
            clientId: config.openidParameters["client-id"],
            authorizationUrl: config.openidParameters["authorization-url"],
            authorizationUrlExtraParameters:
              config.openidParameters["authorization-url-extra-parameters"],
            scope: config.openidParameters.scope || "openid",
          };
        },
        hydratePasswordParams: (params) => {
          params.set("application_id", this.getId());
        },
        hydrateHeaders: (options) => {
          // if there a talkdesk-csrf-token in sessionStorage, add it to the headers
          const csrfToken = sessionStore?.getItem(TALKDESK_CSRF_TOKEN_KEY);
          if (csrfToken) {
            options.headers = options.headers.set(TALKDESK_CSRF_TOKEN_HEADER, csrfToken);
          } else {
            // if there's a talkdesk-csrf-token in localStorage, add it to the headers
            const csrfToken = localStore?.getItem(TALKDESK_CSRF_TOKEN_KEY);
            if (csrfToken) {
              options.headers = options.headers.set(TALKDESK_CSRF_TOKEN_HEADER, csrfToken);
            }
          }
        },
        onHttpResponse(response) {
          // if the response has a talkdesk-csrf-token header, store it in sessionStorage
          const csrfToken = response.headers.get(TALKDESK_CSRF_TOKEN_HEADER);
          if (csrfToken) {
            // store the csrf token in both sessionStorage and localStorage
            sessionStore?.setItem(TALKDESK_CSRF_TOKEN_KEY, csrfToken);
            localStore?.setItem(TALKDESK_CSRF_TOKEN_KEY, csrfToken);
          }
        },
        afterAuthenticate: async () => {
          const config = this.dto.config as IOpenIDConnectApplicationConfig;
          // add talkdesk header to app
          // https://docs.talkdesk.com/docs/talkdesk-header
          const scriptSrc = config.publicParameters?.talkdesk?.["widget-url"];
          if (!scriptSrc) {
            return;
          }
          await loadScript(scriptSrc);
          // eslint-disable-next-line
          const talkdesk = window["TalkdeskConnect"];
          talkdesk?.init();
        },
        config: dto.config,
      }
    );

    this.mAnalyticsStrategy = new NoAnalyticsStrategy();
    this.mQueueStrategy = new NoQueueStrategy();
    this.mTicketStrategy = new StandaloneInvitationStrategy(
      this.getId(),
      this.getConfig()?.publicParameters,
      this.getConfig()?.agentParameters
    );
    this.mNotificationStrategy = BrowserNotificationStrategy.singleton();
    this.mIntegrationStrategy = new TalkdeskIntegrationStrategy(
      this.userService,
      this.injector.get(HttpClient),
      this.getConfig(),
      this.injector.get(AuviousRtcService)
    );
    this.mConversationStrategy = new TalkdeskConversationStrategy(
      this.injector.get(HttpClient),
      this.userService
    );
    this.mInteractionStrategy = new TalkdeskInteractionStrategy(
      this.injector.get(DigitalConnectService),
      this.injector.get(ProtectedTicketService)
    );
  }

  supportsInteractions(): boolean {
    return this.getConfig().agentParameters[AgentParam.DIGITAL_CONNECT_ENABLED]; // true to support digital-connect
  }

  supportsInteractionMetrics(): boolean {
    return false;
  }

  getId(): string {
    return this.dto.id;
  }

  registerLifecycleEventHandlers() {}

  getDto(): IApplicationDTO {
    return this.dto;
  }

  getType(): ApplicationTypeEnum {
    return ApplicationTypeEnum.TalkdeskOpenID;
  }

  getOrganizationId(): string {
    return this.dto.organizationId;
  }

  getConfig(): IOpenIDConnectApplicationConfig {
    return this.dto.config;
  }

  getCreatedAt(): Date {
    return new Date(this.dto.createdAt);
  }

  getVersion(): number {
    return this.dto.version;
  }

  getFilterBackgrounds() {
    return [];
  }

  canCallWithoutActiveInteraction(): boolean {
    return this.getConfig().agentParameters?.[
      AgentParam.DIGITAL_CONNECT_ENABLED
    ]
      ? !this.getConfig().agentParameters?.[
          AgentParam.VIDEO_CALL_REQUIRES_INTERACTION
        ]
      : true;
  }

  isReady(): boolean {
    return true;
  }

  authenticationStrategy(): IAuthenticationStrategy {
    return this.mAuthenticationStrategy;
  }

  interactionStrategy(): IInteractionStrategy {
    return this.mInteractionStrategy;
  }

  queueStrategy(): IQueueStrategy {
    return this.mQueueStrategy;
  }

  analyticsStrategy(): IAnalyticsStrategy {
    return this.mAnalyticsStrategy;
  }

  ticketStrategy(): IInvitationStrategy {
    return this.mTicketStrategy;
  }

  notificationStrategy(): INotificationStrategy {
    return this.mNotificationStrategy;
  }

  integrationStrategy(): IIntegrationStrategy {
    return this.mIntegrationStrategy;
  }

  conversationStrategy(): IConversationStrategy {
    return this.mConversationStrategy;
  }
}
